/**
 * CancellationModal Component
 *
 * Multi-step customer cancellation flow:
 * Step 1: Refund estimate preview
 * Step 2: Reason selection + confirm
 * Step 3: Result (approved, pending review, or error)
 *
 * API Endpoints:
 *   GET  /api/v1/bookings/:bookingId/cancellation-estimate
 *   POST /api/v1/bookings/:bookingId/cancel
 *
 * Story: NEW-002-FE - Customer Cancellation Request Flow (UI)
 * MVP: 1.5.2 - Booking Payment Domain Model
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import apiRequest from '../../services/api';
import Modal from '../shared/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import {
  CheckCircle,
  Clock,
  XCircle,
  ArrowLeft,
  DollarSign
} from 'lucide-react';

const CANCEL_REASONS = [
  { value: 'customer_request', label: 'I changed my mind' },
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'weather_concern', label: 'Weather concerns' },
  { value: 'health_issue', label: 'Health issue' },
  { value: 'financial_hardship', label: 'Financial hardship' },
  { value: 'other', label: 'Other reason' },
];

export default function CancellationModal({ isOpen, onClose, bookingId, onCancelled }) {
  const [step, setStep] = useState(1);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [result, setResult] = useState(null);

  const fetchEstimate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(
        `/api/v1/bookings/${bookingId}/cancellation-estimate`
      );
      if (response.success) {
        setEstimate(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load cancellation estimate');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  // Fetch estimate when modal opens
  useEffect(() => {
    if (isOpen && bookingId) {
      fetchEstimate();
    }
    // Reset state when modal opens
    if (isOpen) {
      setStep(1);
      setReason('');
      setReasonDetails('');
      setResult(null);
      setError(null);
    }
  }, [isOpen, bookingId, fetchEstimate]);

  const handleCancel = async () => {
    try {
      setCancelling(true);
      setError(null);
      const response = await apiRequest(`/api/v1/bookings/${bookingId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason, reasonDetails: reasonDetails || undefined }),
      });

      if (response.success) {
        setResult(response.data);
        setStep(3);
        if (onCancelled) {
          onCancelled(response.data);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel booking');
      setStep(3);
      setResult({ status: 'error', message: err.message || 'Failed to cancel booking' });
    } finally {
      setCancelling(false);
    }
  };

  const formatCurrency = (cents) => {
    if (cents === null || cents === undefined) {return '$0.00';}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const handleClose = () => {
    onClose();
  };

  // Step 1: Estimate
  const renderEstimate = () => {
    if (loading) {
      return (
        <div className="py-8">
          <LoadingSpinner message="Calculating your refund estimate..." size="md" color="border-brand-primary" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-6">
          <Alert variant="error" message={error} className="mb-4 text-left" />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Close
          </button>
        </div>
      );
    }

    if (!estimate) {return null;}

    return (
      <div className="space-y-4">
        {/* Grace Period Banner */}
        {estimate.is48HourGracePeriod && (
          <Alert variant="success" message="Free cancellation — full refund (48-hour grace period)" className="p-3" />
        )}

        {/* Policy Info */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Policy Applied</span>
            <span className="font-medium text-gray-900">{estimate.policyApplied}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Days Until Trip</span>
            <span className="font-medium text-gray-900">{estimate.daysBeforeTrip} days</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Refund Tier</span>
            <span className="font-medium text-gray-900">{estimate.refundPercentage}% refund</span>
          </div>
        </div>

        {/* Refund Amounts */}
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900">You&apos;ll Receive</span>
            </div>
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(estimate.customerRefundCents)}
            </span>
          </div>
          {estimate.customerRefundCents < (estimate.totalPaidCents || 0) && (
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-gray-600">Non-refundable</span>
              <span className="text-sm font-medium text-red-600">
                {formatCurrency((estimate.totalPaidCents || 0) - estimate.customerRefundCents)}
              </span>
            </div>
          )}
        </div>

        {/* Info message */}
        {estimate.message && (
          <p className="text-xs text-gray-500">{estimate.message}</p>
        )}
      </div>
    );
  };

  // Step 2: Reason & Confirm
  const renderConfirm = () => (
    <div className="space-y-4">
      {/* Reason Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason for Cancellation
        </label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
        >
          <option value="">Select a reason...</option>
          {CANCEL_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {/* Details Textarea */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Additional Details
        </label>
        <textarea
          value={reasonDetails}
          onChange={(e) => setReasonDetails(e.target.value)}
          placeholder="Tell us more (optional)"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
        />
      </div>

      {/* Compact Refund Summary */}
      {estimate && (
        <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between text-sm">
          <span className="text-gray-600">Refund Amount</span>
          <span className="font-semibold text-green-600">
            {formatCurrency(estimate.customerRefundCents)} ({estimate.refundPercentage}%)
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <Alert variant="error" message={error} className="p-3" />
      )}
    </div>
  );

  // Step 3: Result
  const renderResult = () => {
    if (!result) {return null;}

    if (result.status === 'approved') {
      return (
        <div className="text-center py-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Booking Cancelled</h3>
          <p className="text-gray-600 mb-1">
            Your refund of{' '}
            <span className="font-semibold text-green-600">
              {result.refund?.customerRefundCents
                ? formatCurrency(result.refund.customerRefundCents)
                : 'the applicable amount'}
            </span>{' '}
            will be processed within 5-10 business days.
          </p>
        </div>
      );
    }

    if (result.status === 'pending_review') {
      return (
        <div className="text-center py-6">
          <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancellation Under Review</h3>
          <p className="text-gray-600">
            Your cancellation request has been submitted for review.
            We&apos;ll notify you by email within 24-48 hours.
          </p>
        </div>
      );
    }

    // Error state
    return (
      <div className="text-center py-6">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancellation Failed</h3>
        <p className="text-red-600">{result.message || 'An error occurred. Please try again.'}</p>
      </div>
    );
  };

  // Footer buttons per step
  const renderFooter = () => {
    if (step === 1) {
      if (loading || error) {return null;}
      return (
        <>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Never Mind
          </button>
          <button
            onClick={() => setStep(2)}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark transition"
          >
            Continue
          </button>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <button
            onClick={() => {
              setStep(1);
              setError(null);
            }}
            className="flex items-center space-x-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Go Back</span>
          </button>
          <button
            onClick={handleCancel}
            disabled={!reason || cancelling}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <XCircle className="w-4 h-4" />
            <span>{cancelling ? 'Cancelling...' : 'Cancel This Booking'}</span>
          </button>
        </>
      );
    }

    // Step 3
    return (
      <button
        onClick={handleClose}
        className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
      >
        Close
      </button>
    );
  };

  const stepTitles = {
    1: 'Cancel Booking',
    2: 'Confirm Cancellation',
    3: result?.status === 'approved'
      ? 'Cancelled'
      : result?.status === 'pending_review'
        ? 'Under Review'
        : 'Cancellation Result',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 3 ? handleClose : onClose}
      title={stepTitles[step]}
      footer={renderFooter()}
      closeOnEscape={step !== 2}
      closeOnBackdrop={false}
    >
      {step === 1 && renderEstimate()}
      {step === 2 && renderConfirm()}
      {step === 3 && renderResult()}
    </Modal>
  );
}

CancellationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bookingId: PropTypes.string.isRequired,
  onCancelled: PropTypes.func,
};
